import React, { useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import PrimaryButtonV2 from '../UI/Buttons/PrimaryButtonV2';
import switchIcon from '../../assets/icons/switch-camera.svg';
import classes from './Webcam.module.css';
import { numberToSeconds } from '../../utils/miscUtils';
import RecordRTC from 'recordrtc';

import useSpeechRecognition from '../../hooks/useSpeechRecognition';
import { getSpeechLocale } from '../../utils/dateLocale';
import { useTranslation } from '../../hooks/useTranslations';

const FACING_MODE_USER = 'user';
const FACING_MODE_ENVIRONMENT = 'environment';

export type VideoDataProps = {
  blob: Blob | undefined;
  videoUrl: string;
  transcript: string;
};
type Props = {
  onSaveVideo: (data: VideoDataProps) => void;
};

const VideoCapture: React.FC<Props> = ({ onSaveVideo }) => {
  const webcamRef = React.useRef(null);
  const { translations } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<RecordRTC>();
  const [facingMode, setFacingMode] = useState(FACING_MODE_USER);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const initialState = { blob: undefined, videoUrl: '', transcript: '' };
  const [videoData, setVideoData] = useState<VideoDataProps>(initialState);

  const videoConstraints = {
    width: 500,
    height: 378,
    facingMode: facingMode,
  };
  const lang = getSpeechLocale(localStorage.getItem('lang') || 'en');
  const { transcript, startListening, stopListening } = useSpeechRecognition(lang);

  const handleStartCaptureClick = async () => {
    startListening();
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    if (webcamRef.current && stream) {
      (webcamRef.current as any).video.srcObject = stream;
    }
    const recorder = new RecordRTC(stream, {
      type: 'video',
      mimeType: 'video/mp4',
      recorderType: RecordRTC.MediaStreamRecorder,
      audioBitsPerSecond: 64000,
      videoBitsPerSecond: 128000,
    });

    recorder.startRecording();
    setMediaRecorder(recorder);
    setRecording(true);
    setStartTime(performance.now());
  };

  const handleStopCaptureClick = () => {
    stopListening();
    mediaRecorder &&
      mediaRecorder.stopRecording(() => {
        const blob = mediaRecorder.getBlob();
        const videoUrl = URL.createObjectURL(blob);
        setVideoData({ blob: blob, videoUrl: videoUrl, transcript: '' });
      });
    setRecording(false);
  };

  const handleSwitch = useCallback(() => {
    setFacingMode((prevState) =>
      prevState === FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER
    );
  }, []);
  React.useEffect(() => {
    if (startTime !== null && recording) {
      const intervalId = setInterval(() => {
        const currentTime = performance.now();
        const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
        setElapsedTime(elapsedSeconds);
      }, 1000); // Update every second
      return () => clearInterval(intervalId);
    }
  }, [startTime, recording]);

  const handleSubmit = async () => {
    const updatedVideoData = {
      ...videoData,
      transcript: transcript,
    };
    setVideoData(updatedVideoData);
    onSaveVideo(updatedVideoData);
  };

  return (
    <div>
      <div className="App">
        {videoData.blob === undefined ? (
          <div className={classes.previewContainer}>
            <Webcam
              audio={true}
              muted
              height={378}
              ref={webcamRef}
              width={500}
              videoConstraints={videoConstraints}
            />
            {recording ? (
              <div className={classes.timer}>{numberToSeconds(elapsedTime)}</div>
            ) : (
              <div className={classes.switchButtonContainer} onClick={handleSwitch}>
                <img src={switchIcon} alt={'switch-camera'} />
              </div>
            )}
          </div>
        ) : (
          <video src={videoData.videoUrl} controls width={500} height={378} />
        )}
      </div>

      <div className="App">
        {videoData.blob !== undefined ? (
          <>
            {/* <div className={classes.transcriptionContainer}>
              <div
                className={classes.transcriptionTitle}
                style={transcript ? {} : { color: '#D9D9D9' }}
              >
                {translations('transcription')}
              </div>
              <textarea value={transcript} onChange={(e) => updatedTranscription(e.target.value)}>
                {transcript}
              </textarea>
            </div> */}
            <div className={classes.container}>
              <PrimaryButtonV2
                text={translations('save')}
                buttonStyle={{
                  width: '100%',
                  height: 60,
                  marginTop: 20,
                }}
                onClick={handleSubmit}
                disabled={false}
              />
              <PrimaryButtonV2
                text={translations('retake.recording')}
                containerStyle={{
                  marginTop: 20,
                }}
                invertedColors
                buttonStyle={{
                  width: '100%',
                  border: 'none',
                  backgroundColor: 'inherit',
                  boxShadow: 'none',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setVideoData(initialState);
                }}
                disabled={false}
              />
            </div>
          </>
        ) : (
          <div className={classes.buttonsContainer}>
            {recording ? (
              <PrimaryButtonV2
                text={translations('stop.recording')}
                containerStyle={{
                  width: '100%',
                }}
                buttonStyle={{
                  width: '100%',
                  height: '60px',
                }}
                onClick={handleStopCaptureClick}
                disabled={false}
              />
            ) : (
              <PrimaryButtonV2
                text={translations('record')}
                containerStyle={{
                  padding: 0,
                  width: '100%',
                }}
                buttonStyle={{
                  width: '100%',
                  height: '60px',
                }}
                onClick={handleStartCaptureClick}
                disabled={false}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoCapture;
